/**
 * What each of the book's twenty terrains looks like, and how a grid becomes pixels.
 *
 * **The correspondence is an invention of this application, so it is declared in
 * one table.** The book prints movement costs, not colours; nothing in the
 * corpus says a moor is purple. That makes this the fourth declared mapping
 * over the same vocabulary — beside the biomes, the lost-check surroundings and
 * the encounter-cover rows — and it is held to the same rule: all twenty are
 * answered, and a terrain the book prints with no entry here is a test failure
 * rather than a hole somebody notices as a gap in a picture.
 *
 * Unlike the other three, a miss here does **not** throw. Those decide numbers,
 * where a plausible wrong answer is the failure mode; this decides a colour, and
 * taking the whole map down because one of twenty terrains lacked a hex would
 * cost the table more than drawing it grey and saying how many cells were grey.
 */

/** The book's own terrain keys, as `travelTerrains()` prints them. */
export type TerrainName = string;

/**
 * Terrain to colour.
 *
 * Grouped by kind and shaded within the group, because the question an Owner
 * asks this picture is "what is the party crossing" and the answer is wanted at
 * a glance: all three forests are green and the heavy one is darkest, so the
 * gradient itself carries the cost. Hue separates the kinds; lightness separates
 * the grades within a kind, which is the ordering the movement table already has.
 *
 * Muted deliberately. This is painted *under* a hand-drawn map's rivers and
 * pins, and a saturated wash would fight the artwork it is meant to sit beneath.
 */
export const TERRAIN_COLOUR: Readonly<Record<TerrainName, readonly [number, number, number]>> = {
  // Open, walkable ground — the cheapest rows in the table.
  clearFarmland: [214, 205, 162],
  grasslandPlains: [190, 199, 138],
  scrubBrushland: [172, 175, 122],

  // Forest, darkening with the cost.
  forestLight: [146, 174, 118],
  forestMedium: [104, 145, 96],
  forestHeavy: [66, 110, 74],

  // Jungle — green too, but warmer, so it is not read as another forest grade.
  jungleMedium: [96, 148, 86],
  jungleHeavy: [58, 112, 62],

  // Relief, lightening upward the way a hypsometric map does.
  hillsRolling: [188, 166, 126],
  hillsSteep: [170, 143, 104],
  mountainsLow: [166, 150, 140],
  mountainsMedium: [186, 176, 170],
  mountainsHigh: [216, 212, 210],

  // Dry country.
  desertSand: [230, 211, 158],
  desertRocky: [200, 176, 140],
  barrenWasteland: [178, 164, 148],

  // Wet and cold.
  marshSwamp: [120, 138, 118],
  moor: [140, 132, 150],
  tundra: [196, 200, 196],
  glacier: [226, 234, 240],
};

/** Drawn for a cell whose terrain has no entry above, and for `-1` unmapped. */
export const UNMAPPED_COLOUR: readonly [number, number, number] = [148, 148, 148];

export interface TerrainGridView {
  readonly width: number;
  readonly height: number;
  readonly legend: readonly string[];
  readonly cells: readonly number[];
}

export interface PaintedTerrain {
  /**
   * RGBA, four bytes a cell, row-major — straight into `putImageData`.
   *
   * Backed by an `ArrayBuffer` explicitly, not the `ArrayBufferLike` the bare
   * constructor infers: `ImageData` will not take a view that might be over a
   * `SharedArrayBuffer`, so leaving it inferred fails to compile at the one
   * place this whole array exists to reach.
   */
  readonly pixels: Uint8ClampedArray<ArrayBuffer>;
  readonly width: number;
  readonly height: number;
}

/**
 * A grid to RGBA bytes.
 *
 * **Extracted as a pure function for the same reason `pointFromClick` is.** The
 * client's tests render statically with no DOM, so nothing in a test ever gets a
 * canvas — and this is arithmetic that is wrong in ways a rendering test cannot
 * see. A row/column transposition draws a plausible map of the wrong world; an
 * off-by-one in the stride draws a diagonal smear. Both look like a bad import.
 *
 * Unmapped cells are left fully transparent rather than grey: `-1` is mostly sea
 * and everything outside the exported world — 39.8% of the first real import —
 * and painting that opaque would hide whichever map the Owner uploaded under a
 * grey field covering two fifths of it.
 *
 * **It returns pixels and nothing else.** An earlier cut also counted the
 * unmapped and the uncoloured cells and no caller read either, which is this
 * repo's own written-tested-never-called shape; what a reader actually needs to
 * be told is *which* ground came out grey, and `unrecognisedTerrains` answers
 * that off the legend without sweeping the cells at all.
 */
export function paintTerrain(grid: TerrainGridView): PaintedTerrain {
  const { width, height, legend, cells } = grid;
  const pixels = new Uint8ClampedArray(new ArrayBuffer(width * height * 4));

  // Resolved once per legend entry, not once per cell: the legend is at most
  // twenty long and the grid is twenty thousand cells.
  const colours = legend.map((terrain) => TERRAIN_COLOUR[terrain]);

  for (let index = 0; index < width * height; index += 1) {
    const cell = cells[index] ?? -1;
    const offset = index * 4;

    // Transparent, and deliberately without counting it: the uploaded image
    // shows through, which is the whole point.
    if (cell < 0 || cell >= legend.length) continue;

    const colour = colours[cell] ?? UNMAPPED_COLOUR;
    pixels[offset] = colour[0];
    pixels[offset + 1] = colour[1];
    pixels[offset + 2] = colour[2];
    pixels[offset + 3] = 255;
  }

  return { pixels, width, height };
}

/**
 * The ground this palette has no colour for, so a reader can be told.
 *
 * **Off the legend, not the cells**: the legend is at most twenty entries and
 * names every terrain the import found, so this is twenty lookups rather than a
 * sweep of twenty thousand. It exists because grey is indistinguishable from a
 * *terrain* that happens to be grey — a glacier and a tundra are both pale — so
 * a silently uncoloured ground reads as ordinary map rather than as a gap.
 *
 * Normally empty: the completeness test makes it impossible for a terrain the
 * book prints to land here, so anything it returns came out of a grid written
 * before that terrain was named, or edited by hand.
 */
export function unrecognisedTerrains(grid: TerrainGridView): readonly string[] {
  return grid.legend.filter((terrain) => !TERRAIN_COLOUR[terrain]);
}

/**
 * The terrains present in a grid, most land first — the legend a reader needs.
 *
 * Counted rather than listed off the legend, because the legend carries every
 * terrain the *import* found and an Owner reading the picture wants the ones
 * actually on it, biggest first. A terrain occupying four cells of twenty thousand
 * is noise in a key.
 */
export function terrainTally(grid: TerrainGridView): { name: string; cells: number }[] {
  const counts = new Map<string, number>();
  for (const cell of grid.cells) {
    if (cell < 0 || cell >= grid.legend.length) continue;
    const name = grid.legend[cell]!;
    counts.set(name, (counts.get(name) ?? 0) + 1);
  }
  return [...counts.entries()]
    .map(([name, cells]) => ({ name, cells }))
    .sort((a, b) => b.cells - a.cells);
}
